/**
 * Chittera reveal.
 *
 * Fades and lifts section content as it scrolls into view.
 *
 * The one rule this file exists to keep: content is only ever hidden by code
 * that has already proven it can un-hide it. Every early return below happens
 * before a single element is touched, so a browser without IntersectionObserver,
 * a visitor who asked for reduced motion, or a throw inside setup all leave the
 * page rendering exactly as it would with this script absent.
 *
 * An earlier version of this effect lived purely in CSS on a view timeline. It
 * hid content in the stylesheet and relied on the timeline to bring it back,
 * which meant anywhere the timeline never advanced the content was simply gone.
 * Hence the order of operations here.
 */
(function () {
  'use strict';

  var PENDING = 'chittera-reveal-pending';
  var VISIBLE = 'chittera-reveal-visible';

  /* Direct children of a section are the natural unit: a heading block, a media
     block, a grid. Anything finer and the page twitches. */
  var TARGETS = '.section > *';
  var SKIP = '.section-background';

  /* Groups whose children cascade instead of the group arriving as one block. */
  var STAGGER = '.trust-bar__grid, .process__grid';
  var STEP_MS = 70;
  var MAX_STEPS = 6;

  if (!('IntersectionObserver' in window)) return;
  if (!window.matchMedia) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var observer = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        show(entries[i].target);
        observer.unobserve(entries[i].target);
      }
    },
    {
      /* Start the reveal a little before the element is flush with the bottom
         edge, so it is settling as it arrives rather than after. */
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.01,
    }
  );

  function show(element) {
    element.classList.remove(PENDING);
    element.classList.add(VISIBLE);
  }

  /**
   * @param {ParentNode} root
   * @returns {Element[]}
   */
  function collect(root) {
    var found = [];
    var candidates = root.querySelectorAll(TARGETS);

    for (var i = 0; i < candidates.length; i++) {
      var element = candidates[i];
      if (element.matches(SKIP)) continue;

      var group = element.matches(STAGGER) ? element : element.querySelector(STAGGER);

      if (group && group.children.length) {
        /* Reveal the items, not the container around them — otherwise the group
           fades in while its children fade in inside it. */
        for (var j = 0; j < group.children.length; j++) {
          var child = group.children[j];
          child.style.setProperty('--chittera-reveal-delay', Math.min(j, MAX_STEPS) * STEP_MS + 'ms');
          found.push(child);
        }
      } else {
        found.push(element);
      }
    }

    return found;
  }

  /**
   * @param {ParentNode} root
   */
  function init(root) {
    var targets = collect(root);
    var fold = window.innerHeight;

    for (var i = 0; i < targets.length; i++) {
      var element = targets[i];
      if (element.classList.contains(PENDING) || element.classList.contains(VISIBLE)) continue;

      /* Anything already on screen rides the page entrance in chittera-brand
         instead of running its own reveal, so the first viewport does not
         animate twice. */
      if (element.getBoundingClientRect().top < fold) continue;

      element.classList.add(PENDING);
      observer.observe(element);
    }
  }

  /**
   * @param {ParentNode} root
   */
  function start(root) {
    try {
      init(root);
    } catch (error) {
      /* Whatever went wrong, do not leave anything held at zero opacity. */
      var stuck = document.querySelectorAll('.' + PENDING);
      for (var i = 0; i < stuck.length; i++) show(stuck[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      start(document);
    });
  } else {
    start(document);
  }

  /* The theme editor swaps a section's markup without reloading the page. */
  document.addEventListener('shopify:section:load', function (event) {
    start(event.target instanceof Element ? event.target : document);
  });
})();
